import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  ActivityIndicator,
  Modal,
  Pressable,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Image } from 'expo-image';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { fetchVisibleStudents } from '../utils/fetchVisibleStudents';
import {
  canSeeGradingTile,
  canChangeAnyStudentGrade,
  canSelectStudentForGrading,
  getAssignableGradesForStudent,
  isStudentCurrentGrade,
} from '../utils/gradingAccess';
import { getSyllabusGradeIconSource } from '../utils/syllabusGradeIcons';

const LIGHT_CREAM = '#faf8f4';

function studentDisplayName(student) {
  if (!student) return 'Select student';
  const name = String(student.student_name || '').trim();
  if (name) return name;
  return String(student.username || 'Student');
}

export default function ChangeGradePage({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [students, setStudents] = useState([]);
  const [grades, setGrades] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedGradeId, setSelectedGradeId] = useState(null);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [loadingGrades, setLoadingGrades] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  useEffect(() => {
    const uid = route?.params?.userId || 1;
    fetch(`${getApiUrl()}/api/users/${uid}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => data && setCurrentUser(data))
      .catch(() => {});
  }, [route?.params?.userId]);

  const loadStudents = useCallback(async () => {
    if (!currentUser?.id) return;
    setLoadingStudents(true);
    try {
      const { students: rows } = await fetchVisibleStudents({ requester: currentUser });
      const list = (Array.isArray(rows) ? rows : []).filter((s) =>
        canSelectStudentForGrading(currentUser, s, grades)
      );
      setStudents(list);
    } catch {
      setStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  }, [currentUser, grades]);

  const loadGrades = useCallback(async () => {
    setLoadingGrades(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/grades`);
      if (res.ok) {
        const data = await res.json();
        setGrades(Array.isArray(data) ? data : []);
      } else {
        setGrades([]);
      }
    } catch {
      setGrades([]);
    } finally {
      setLoadingGrades(false);
    }
  }, []);

  useEffect(() => {
    if (currentUser?.id) void loadStudents();
  }, [currentUser?.id, loadStudents]);

  useEffect(() => {
    void loadGrades();
  }, [loadGrades]);

  const selectedStudent = useMemo(
    () => students.find((s) => s.id === selectedStudentId) ?? null,
    [students, selectedStudentId]
  );

  const assignableGrades = useMemo(() => {
    if (!selectedStudent) return [];
    return getAssignableGradesForStudent(currentUser, selectedStudent, grades);
  }, [currentUser, selectedStudent, grades]);

  const assignableGradeIds = useMemo(
    () => new Set(assignableGrades.map((g) => g.id)),
    [assignableGrades]
  );

  const selectedGrade = useMemo(
    () => grades.find((g) => g.id === selectedGradeId) ?? null,
    [grades, selectedGradeId]
  );

  const hasNewGradeSelected = useMemo(() => {
    if (!selectedStudent || !selectedGrade) return false;
    if (isStudentCurrentGrade(selectedStudent, selectedGrade, grades)) return false;
    return assignableGradeIds.has(selectedGrade.id);
  }, [selectedStudent, selectedGrade, grades, assignableGradeIds]);

  const canUpdate = Boolean(hasNewGradeSelected && !saving);

  const handleSelectStudent = (student) => {
    setSelectedStudentId(student.id);
    setSelectedGradeId(null);
    setShowStudentDropdown(false);
    setStatusMessage(null);
  };

  const handleReset = () => {
    setSelectedStudentId(null);
    setSelectedGradeId(null);
    setStatusMessage(null);
    setShowStudentDropdown(false);
  };

  const handleUpdate = () => {
    if (!selectedStudent || !selectedGradeId || !currentUser?.id || saving) return;
    const grade = grades.find((g) => g.id === selectedGradeId);
    if (!grade) return;
    void submitGradeChange(grade);
  };

  const submitGradeChange = async (grade) => {
    setSaving(true);
    setStatusMessage(null);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/students/${encodeURIComponent(String(selectedStudentId))}/change-grade`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            requester_id: currentUser.id,
            grade_id: grade.id,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatusMessage({ type: 'error', text: data.error || 'Failed to change grade' });
        return;
      }

      setStudents((prev) =>
        prev.map((s) =>
          s.id === selectedStudentId
            ? {
                ...s,
                student_grade: data.student_grade,
                student_grade_rank: data.student_grade_rank,
              }
            : s
        )
      );
      setSelectedGradeId(null);
      setStatusMessage({ type: 'success', text: 'Student Grade Updated.' });
    } catch (e) {
      setStatusMessage({
        type: 'error',
        text: e?.message || 'Failed to change grade',
      });
    } finally {
      setSaving(false);
    }
  };

  const styles = getStyles(theme);
  const allowed = canSeeGradingTile(currentUser);
  const fullAccess = canChangeAnyStudentGrade(currentUser);

  if (currentUser && !allowed) {
    return (
      <View style={styles.container}>
        <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
        <View style={styles.header}>
          <View style={styles.hamburgerContainer}>
            <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
          </View>
          <View style={styles.breadcrumbContainer}>
            <Text style={styles.breadcrumb}>Change Grade</Text>
          </View>
          <View style={styles.profileIconContainer}>
            <ProfileIcon navigation={navigation} user={currentUser} />
          </View>
        </View>
        <View style={styles.deniedWrap}>
          <Text style={styles.deniedText}>
            Change Grade is available to named graders and 1st degree black belts and above.
          </Text>
        </View>
        <Stopwatch ref={stopwatchRef} />
      </View>
    );
  }

  return (
    <View
      style={styles.container}
      data-screen="change-grade"
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
      {...(Platform.OS === 'web' ? { dataSet: { changeGradeScreen: 'true' } } : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />

      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Change Grade</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        <View style={styles.titleSection}>
          <Text style={styles.title}>Change Grade</Text>
          <Text style={styles.subtitle}>
            {fullAccess
              ? 'Select a student and choose their new grade.'
              : 'You may upgrade students below your rank to a grade below your own.'}
          </Text>
        </View>

        {statusMessage ? (
          <View
            style={[
              styles.feedbackToast,
              statusMessage.type === 'success'
                ? styles.feedbackToastSuccess
                : styles.feedbackToastError,
            ]}
            {...(Platform.OS === 'web'
              ? {
                  dataSet: {
                    changeGradeFeedbackToast: 'true',
                    changeGradeFeedbackToastType: statusMessage.type,
                  },
                }
              : {})}
          >
            <Text
              style={styles.feedbackToastMessage}
              {...(Platform.OS === 'web'
                ? { dataSet: { changeGradeFeedbackToastText: 'true' } }
                : {})}
            >
              {statusMessage.text}
            </Text>
            <TouchableOpacity
              style={styles.feedbackToastClose}
              onPress={() => setStatusMessage(null)}
              {...(Platform.OS === 'web'
                ? { dataSet: { changeGradeFeedbackToastClose: 'true' } }
                : {})}
            >
              <Text style={styles.feedbackToastCloseText}>✕</Text>
            </TouchableOpacity>
          </View>
        ) : null}

        <View style={styles.fieldContainer}>
          <Text style={styles.label}>Student</Text>
          <TouchableOpacity
            style={styles.pickTrigger}
            onPress={() => setShowStudentDropdown(true)}
            disabled={loadingStudents}
            {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
          >
            {loadingStudents ? (
              <ActivityIndicator size="small" color={theme.colors.primary || '#d4af37'} />
            ) : (
              <>
                <Text
                  style={[
                    styles.pickTriggerText,
                    !selectedStudentId && styles.pickTriggerPlaceholder,
                  ]}
                  numberOfLines={1}
                >
                  {selectedStudent
                    ? `${studentDisplayName(selectedStudent)}${
                        selectedStudent.student_grade
                          ? ` (${selectedStudent.student_grade})`
                          : ''
                      }`
                    : students.length === 0
                      ? 'No eligible students'
                      : 'Select student'}
                </Text>
                <Text style={styles.pickTriggerArrow}>▼</Text>
              </>
            )}
          </TouchableOpacity>
        </View>

        {selectedStudent ? (
          <View style={styles.gradeListSection}>
            <Text style={styles.label}>Grades</Text>
            {loadingGrades ? (
              <ActivityIndicator
                style={styles.gradeListLoading}
                color={theme.colors.primary || '#d4af37'}
              />
            ) : (
              grades.map((grade) => {
                const isCurrent = isStudentCurrentGrade(selectedStudent, grade, grades);
                const canAssign = assignableGradeIds.has(grade.id) && !isCurrent;
                const isSelected = selectedGradeId === grade.id;
                return (
                  <TouchableOpacity
                    key={grade.id}
                    style={[
                      styles.gradeRow,
                      isCurrent && styles.gradeRowCurrent,
                      isSelected && styles.gradeRowSelected,
                      !canAssign && !isCurrent && styles.gradeRowDisabled,
                    ]}
                    onPress={() => {
                      if (canAssign && !saving) setSelectedGradeId(grade.id);
                    }}
                    disabled={!canAssign || saving}
                    activeOpacity={canAssign ? 0.75 : 1}
                    {...(Platform.OS === 'web'
                      ? {
                          dataSet: {
                            changeGradeRow: 'true',
                            changeGradeRowCurrent: isCurrent ? 'true' : 'false',
                            changeGradeRowSelected: isSelected ? 'true' : 'false',
                          },
                        }
                      : {})}
                  >
                    <View style={styles.gradeRowTextCol}>
                      <Text
                        style={[
                          styles.gradeRowName,
                          !canAssign && !isCurrent && styles.gradeRowNameDisabled,
                        ]}
                      >
                        {grade.grade_name}
                        {isCurrent ? ' (current)' : ''}
                      </Text>
                    </View>
                    <Image
                      source={getSyllabusGradeIconSource(grade.grade_name)}
                      style={styles.gradeRowImage}
                      contentFit="contain"
                    />
                  </TouchableOpacity>
                );
              })
            )}
          </View>
        ) : null}

        <View style={styles.buttonContainer}>
          <View
            style={styles.resetBtnShell}
            {...(Platform.OS === 'web' ? { dataSet: { changeGradeResetBtn: 'true' } } : {})}
          >
            <TouchableOpacity
              style={styles.resetBtnInner}
              onPress={handleReset}
              disabled={saving}
              activeOpacity={0.85}
            >
              <Text style={styles.resetBtnText}>Reset</Text>
            </TouchableOpacity>
          </View>
          <View
            style={[styles.updateBtnShell, !canUpdate && styles.updateBtnShellDisabled]}
            {...(Platform.OS === 'web'
              ? {
                  dataSet: {
                    changeGradeUpdateBtn: 'true',
                    changeGradeUpdateBtnDisabled: !canUpdate ? 'true' : 'false',
                  },
                }
              : {})}
          >
            <TouchableOpacity
              style={[styles.updateBtnInner, !canUpdate && styles.updateBtnDisabled]}
              onPress={handleUpdate}
              disabled={!canUpdate}
              activeOpacity={0.85}
            >
              {saving ? (
                <ActivityIndicator color="#000000" size="small" />
              ) : (
                <Text style={styles.updateBtnText}>Update</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>

      <Modal
        visible={showStudentDropdown}
        transparent
        animationType="fade"
        onRequestClose={() => setShowStudentDropdown(false)}
      >
        <View style={styles.modalRoot}>
          <Pressable style={styles.modalBackdrop} onPress={() => setShowStudentDropdown(false)} />
          <View
            style={styles.modalSheet}
            {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
          >
            <Text style={styles.modalTitle}>Select student</Text>
            <ScrollView style={styles.modalScroll} keyboardShouldPersistTaps="handled">
              {students.map((student) => (
                <TouchableOpacity
                  key={student.id}
                  style={[
                    styles.modalRow,
                    selectedStudentId === student.id && styles.modalRowSelected,
                  ]}
                  onPress={() => handleSelectStudent(student)}
                  {...(Platform.OS === 'web'
                    ? {
                        dataSet: {
                          dropdownRow: 'true',
                          changeGradeDropdownRowSelected:
                            selectedStudentId === student.id ? 'true' : 'false',
                        },
                      }
                    : {})}
                >
                  <Text
                    style={[
                      styles.modalRowText,
                      selectedStudentId === student.id && styles.modalRowTextSelected,
                    ]}
                  >
                    {studentDisplayName(student)}
                    {student.student_grade ? ` — ${student.student_grade}` : ''}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
      backgroundColor: 'transparent',
    },
    hamburgerContainer: {
      marginRight: 15,
      backgroundColor: 'transparent',
    },
    breadcrumbContainer: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.heading,
      backgroundColor: 'transparent',
    },
    profileIconContainer: {
      backgroundColor: 'transparent',
    },
    content: {
      flex: 1,
    },
    contentContainer: {
      padding: 20,
      paddingBottom: 40,
      maxWidth: 640,
      alignSelf: 'center',
      width: '100%',
    },
    titleSection: {
      marginBottom: 24,
      alignItems: 'center',
    },
    title: {
      fontSize: 24,
      fontWeight: '700',
      color: theme.colors.heading,
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    subtitle: {
      fontSize: 14,
      color: theme.colors.text,
      opacity: 0.75,
      textAlign: 'center',
      lineHeight: 20,
      backgroundColor: 'transparent',
    },
    fieldContainer: {
      marginBottom: 24,
    },
    label: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 10,
      backgroundColor: 'transparent',
    },
    pickTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 14,
      paddingHorizontal: 14,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.isDarkMode
        ? theme.colors.inputBackground || theme.colors.surface
        : LIGHT_CREAM,
    },
    pickTriggerText: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    pickTriggerPlaceholder: {
      opacity: 0.65,
    },
    pickTriggerArrow: {
      fontSize: 12,
      color: theme.colors.text,
      opacity: 0.7,
      marginLeft: 8,
      backgroundColor: 'transparent',
    },
    gradeListSection: {
      gap: 10,
    },
    gradeListLoading: {
      marginTop: 16,
    },
    gradeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      paddingHorizontal: 14,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: theme.colors.border,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : LIGHT_CREAM,
      marginBottom: 10,
    },
    gradeRowCurrent: {
      borderColor: theme.colors.primary || '#d4af37',
      borderWidth: 3,
      borderStyle: 'solid',
    },
    gradeRowSelected: {
      borderColor: theme.colors.primary || '#d4af37',
      borderWidth: 2,
      borderStyle: 'dashed',
    },
    gradeRowDisabled: {
      opacity: 0.45,
    },
    gradeRowTextCol: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    gradeRowName: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    gradeRowNameDisabled: {
      fontWeight: '400',
    },
    gradeRowImage: {
      width: 56,
      height: 56,
      marginLeft: 12,
    },
    feedbackToast: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingVertical: 12,
      paddingHorizontal: 12,
      borderRadius: 8,
      marginBottom: 20,
      width: '100%',
    },
    feedbackToastSuccess: {
      backgroundColor: '#2e7d32',
    },
    feedbackToastError: {
      backgroundColor: '#c62828',
    },
    feedbackToastMessage: {
      flex: 1,
      color: '#ffffff',
      fontSize: 15,
      lineHeight: 20,
      marginRight: 8,
      paddingRight: 4,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    feedbackToastClose: {
      padding: 4,
      marginTop: -2,
      backgroundColor: 'transparent',
    },
    feedbackToastCloseText: {
      color: '#ffffff',
      fontSize: 18,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    buttonContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: 30,
      gap: 15,
    },
    resetBtnShell: {
      flex: 1,
      borderRadius: 8,
      overflow: 'hidden',
      backgroundColor: '#999999',
    },
    resetBtnInner: {
      padding: 15,
      alignItems: 'center',
      backgroundColor: '#999999',
    },
    resetBtnText: {
      color: '#FFFFFF',
      fontSize: 16,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    updateBtnShell: {
      flex: 1,
      borderRadius: 8,
      overflow: 'hidden',
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    updateBtnShellDisabled: {
      opacity: 0.55,
    },
    updateBtnInner: {
      padding: 15,
      alignItems: 'center',
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    updateBtnDisabled: {
      opacity: 0.7,
    },
    updateBtnText: {
      color: '#000000',
      fontSize: 16,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    modalRoot: {
      flex: 1,
      justifyContent: 'center',
      padding: 24,
    },
    modalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.45)',
    },
    modalSheet: {
      maxHeight: '70%',
      backgroundColor: theme.isDarkMode ? theme.colors.surface : LIGHT_CREAM,
      borderRadius: 12,
      padding: 16,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    modalTitle: {
      fontSize: 17,
      fontWeight: '700',
      color: theme.colors.text,
      marginBottom: 12,
      backgroundColor: 'transparent',
    },
    modalScroll: {
      backgroundColor: 'transparent',
    },
    modalRow: {
      paddingVertical: 14,
      paddingHorizontal: 10,
      borderRadius: 8,
      marginBottom: 6,
      backgroundColor: theme.isDarkMode ? '#3d3d3d' : LIGHT_CREAM,
    },
    modalRowSelected: {
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    modalRowText: {
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    modalRowTextSelected: {
      color: '#FFFFFF',
      fontWeight: '600',
    },
    deniedWrap: {
      flex: 1,
      justifyContent: 'center',
      padding: 32,
    },
    deniedText: {
      fontSize: 16,
      textAlign: 'center',
      color: theme.colors.text,
      lineHeight: 24,
      backgroundColor: 'transparent',
    },
  });
